import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  IconButton,
  Input,
  NumberInput,
  NumberInputField,
  Select,
  Spinner,
  Switch,
  Textarea,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { MdArrowBack, MdHistory, MdPlayArrow } from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";
import { fetchController } from "../../utils/FetchController/fetchController";
import { getHostDomain } from "../../utils/utils";
import WorkflowCanvas from "./WorkflowCanvas";
import { TRIGGER_LABELS } from "./templates";

export default function WorkflowEdit() {
  const baseUrl = getHostDomain();
  const navigate = useNavigate();
  const { workflowId } = useParams();
  const toast = useToast();

  const [wf, setWf] = useState(null);
  const [status, setStatus] = useState("idle");
  const [saving, setSaving] = useState(false);
  const definitionRef = useRef({});
  const [showMeta, setShowMeta] = useState(false);

  const load = useCallback(async () => {
    setStatus("loading");
    try {
      const res = await fetchController(`${baseUrl}/api/v1/workflow/${workflowId}`, "GET");
      const data = res?.data;
      setWf(data);
      definitionRef.current = data?.definition || { nodes: [], edges: [] };
      setStatus("success");
    } catch {
      setStatus("error");
    }
  }, [baseUrl, workflowId]);

  useEffect(() => { load(); }, [load]);

  async function save() {
    setSaving(true);
    try {
      const body = {
        name: wf.name,
        description: wf.description,
        trigger: wf.trigger,
        status: wf.status,
        priority: wf.priority,
        definition: definitionRef.current,
      };
      const res = await fetchController(
        `${baseUrl}/api/v1/workflow/${workflowId}`,
        "PUT",
        body
      );
      setWf(res?.data || wf);
      toast({ title: "Workflow saved", status: "success", duration: 2000 });
    } catch (e) {
      toast({ title: "Save failed", description: e?.message, status: "error" });
    } finally {
      setSaving(false);
    }
  }

  async function manualTrigger() {
    const raw = window.prompt(
      'Trigger payload JSON (optional). Example: {"phone":"+15551234567","booking_time":"2026-05-20T15:00:00"}',
      "{}"
    );
    if (raw === null) return;
    let payload = {};
    try { payload = JSON.parse(raw || "{}"); } catch { toast({ title: "Invalid JSON", status: "error" }); return; }
    try {
      const res = await fetchController(
        `${baseUrl}/api/v1/workflow/${workflowId}/trigger`,
        "POST",
        { trigger_payload: payload }
      );
      const run = res?.data;
      toast({ title: `Triggered — run ${run?.status}`, status: "success", duration: 3000 });
      navigate(`/library/workflows/${workflowId}/runs`);
    } catch (e) {
      toast({ title: "Trigger failed", description: e?.message, status: "error" });
    }
  }

  if (status === "loading" || !wf) {
    return <Flex justify="center" align="center" h="100%"><Spinner /></Flex>;
  }
  if (status === "error") {
    return (
      <Box p={6}>
        <Button leftIcon={<MdArrowBack />} onClick={() => navigate("/library/workflows")}>
          Back
        </Button>
        <Box mt={4} color="red.500">Failed to load workflow.</Box>
      </Box>
    );
  }

  return (
    <Flex direction="column" h="100%">
      {/* Header */}
      <Flex align="center" gap={3} p={3} borderBottom="1px solid" borderColor="gray.200" bg="white">
        <IconButton
          icon={<MdArrowBack />}
          aria-label="Back"
          variant="ghost"
          onClick={() => navigate("/library/workflows")}
        />
        <Box flex="1">
          <Heading size="md">{wf.name}</Heading>
          <Flex gap={2} mt={1} align="center">
            <Badge colorScheme="purple" fontSize="xs">
              {TRIGGER_LABELS[wf.trigger] || wf.trigger}
            </Badge>
            <Badge colorScheme={wf.status === "active" ? "green" : "gray"} fontSize="xs">
              {wf.status}
            </Badge>
            <Box fontSize="xs" color="gray.500">v{wf.version}</Box>
          </Flex>
        </Box>
        <Button variant="outline" size="sm" leftIcon={<MdHistory />}
          onClick={() => navigate(`/library/workflows/${workflowId}/runs`)}>
          Runs
        </Button>
        <Button variant="outline" size="sm" leftIcon={<MdPlayArrow />} onClick={manualTrigger}>
          Trigger
        </Button>
        <Button variant="outline" size="sm" onClick={() => setShowMeta((v) => !v)}>
          {showMeta ? "Hide settings" : "Settings"}
        </Button>
        <Button colorScheme="blue" size="sm" isLoading={saving} onClick={save}>
          Save
        </Button>
      </Flex>

      {/* Metadata panel (collapsible) */}
      {showMeta && (
        <Card variant="outline" m={3}>
          <CardBody>
            <VStack align="stretch" spacing={3}>
              <FormControl>
                <FormLabel>Name</FormLabel>
                <Input value={wf.name || ""} onChange={(e) => setWf({ ...wf, name: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea value={wf.description || ""} onChange={(e) => setWf({ ...wf, description: e.target.value })} rows={2} />
              </FormControl>
              <Flex gap={4}>
                <FormControl maxW="240px">
                  <FormLabel>Trigger</FormLabel>
                  <Select value={wf.trigger} onChange={(e) => setWf({ ...wf, trigger: e.target.value })}>
                    {Object.entries(TRIGGER_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl maxW="160px">
                  <FormLabel>Priority</FormLabel>
                  <NumberInput value={wf.priority ?? 100} onChange={(_, v) => setWf({ ...wf, priority: Number.isNaN(v) ? 100 : v })}>
                    <NumberInputField />
                  </NumberInput>
                </FormControl>
                <FormControl maxW="160px">
                  <FormLabel>Active</FormLabel>
                  <Switch
                    isChecked={wf.status === "active"}
                    onChange={(e) => setWf({ ...wf, status: e.target.checked ? "active" : "paused" })}
                    colorScheme="green"
                  />
                </FormControl>
              </Flex>
            </VStack>
          </CardBody>
        </Card>
      )}

      {/* Canvas */}
      <Box flex="1" minH={0}>
        <WorkflowCanvas
          definition={wf.definition || { nodes: [], edges: [] }}
          onChange={(def) => { definitionRef.current = def; }}
        />
      </Box>
    </Flex>
  );
}
